import './CooledProductsConflictModal.css';

import { EventNames, Modal, ModalVariant } from '@farmasiet/frontendcore-reactcomponents';
import React, { useEffect, useRef, useState } from 'react';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../Resources/Translations';
import { removeItemsFromCart } from '../../Purchase/Cart/Components/CartUtils';
import { CartItemViewModel } from '../../Purchase/Cart/Item/ViewModels/CartItemViewModel.csharp';
import { CartViewModel } from '../../Purchase/Cart/ViewModels/CartViewModel.csharp';
import { ApplicationInsights } from '../ApplicationInsights';
import { CooledProductsConflictEntry } from './CooledProductsConflictEntry/CooledProductsConflictEntry';
import { CooledProductsConflictEventDetail } from './CooledProductsConflictEvent';
import { CooledProductsConflictItem } from './CooledProductsConflictItem';

interface CooledProductsConflictModalProps {
    pictureProfile: PictureProfile;
    removeItemsUrl: string;
}

enum SelectedEntry {
    None,
    This,
    Conflicted
}

export const CooledProductsConflictModal = ({ pictureProfile, removeItemsUrl }: CooledProductsConflictModalProps) => {
    const cartRef = useRef<CartViewModel | null>();
    const [error, setError] = useState<string>();
    const [mainProduct, setMainProduct] = useState<CooledProductsConflictItem>();
    const [sameTemperatureProducts, setSameTemperatureProducts] = useState<CooledProductsConflictItem[]>();
    const [conflictedProducts, setConflictedProducts] = useState<CooledProductsConflictItem[]>();
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [onThisKept, setOnThisKept] = useState<() => void>();
    const [onConflictedKept, setOnConflictedKept] = useState<() => void>();
    const [selectedEntry, setSelectedEntry] = useState(SelectedEntry.None);

    useEffect(() => {
        if (mainProduct) {
            setProps();
        }
    }, [mainProduct]);

    const onCartChanged = (event: CustomEventInit<{ cart: CartViewModel }>) => {
        cartRef.current = event.detail!.cart;
    };

    const mapToCooledProductsConflictItems = (items: CartItemViewModel[]): CooledProductsConflictItem[] => {
        return items.map((x) =>
            x.isRx
                ? {
                      code: x.code,
                      name: x.nameInfo,
                      nameUrl: x.rxProductNameUrl,
                      image: x.image.picture,
                      imageUrl: x.rxProductImageUrl,
                      isRx: true,
                      isCooled: x.isCooled
                  }
                : {
                      code: x.code,
                      name: x.nameInfo,
                      image: x.image.picture,
                      isRx: false,
                      isCooled: x.isCooled
                  }
        );
    };

    const getConflictedItems = (isMainProductCooled: boolean, sameTemperatureProducts: boolean) => {
        let conflictItems: CartItemViewModel[] | undefined;

        if (sameTemperatureProducts) {
            conflictItems = cartRef.current!.items.filter((i) => i.isCooled === isMainProductCooled && i.canBeCooled === isMainProductCooled);
        } else {
            conflictItems = cartRef.current!.items.filter((i) => i.isCooled !== isMainProductCooled && i.canBeCooled !== isMainProductCooled);
        }

        return conflictItems ? mapToCooledProductsConflictItems(conflictItems) : [];
    };

    const setProps = () => {
        const sameTemperaturesConflictedItemsArray = getConflictedItems(mainProduct!.isCooled, true);
        sameTemperaturesConflictedItemsArray.unshift(mainProduct!);

        setSameTemperatureProducts(sameTemperaturesConflictedItemsArray);
        setConflictedProducts(getConflictedItems(mainProduct!.isCooled, false));
    };

    const onConflict = (event: CustomEventInit<CooledProductsConflictEventDetail>) => {
        if (!cartRef.current || !event.detail) {
            return;
        }

        setMainProduct(event.detail.product);
        setOnThisKept(() => event.detail!.onThisKept);
        setOnConflictedKept(() => event.detail!.onConflictedKept);
        setIsVisible(true);
    };

    const onClose = () => {
        if (mainProduct && mainProduct.isRx) {
            removeItems([mainProduct.code], onConflictedKept);
        }
        clearModal();
    };

    const clearModal = () => {
        setIsVisible(false);
        setOnConflictedKept(undefined);
        setOnThisKept(undefined);
        setConflictedProducts(undefined);
        setMainProduct(undefined);
        setSameTemperatureProducts(undefined);
        setError(undefined);
        setSelectedEntry(SelectedEntry.None);
    };

    const handleSelect = (entry: SelectedEntry) => {
        setSelectedEntry(entry);
        setError(undefined);
    };

    const removeItems = (productCodes: string[], onProductsKept: (() => void) | undefined) => {
        setIsLoading(true);
        removeItemsFromCart(productCodes, removeItemsUrl, cartRef.current!, (cart) => {
            cartRef.current = cart;
        })
            .then((success) => {
                if (!success) {
                    return;
                }

                if (onProductsKept) {
                    onProductsKept();
                }
                setIsVisible(false);
                clearModal();
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleConfirm = () => {
        switch (selectedEntry) {
            case SelectedEntry.None:
                setError(getTranslation(keys['modal.cooledProducts.errors.nothingSelected']));
                break;
            case SelectedEntry.This:
                ApplicationInsights.TrackEvent(`cooledProductConflictResolution`, {
                    kept: 'this',
                    keptCooled: mainProduct?.isCooled
                });
                removeItems(
                    conflictedProducts!.map((x) => x.code),
                    onThisKept
                );
                break;
            case SelectedEntry.Conflicted: {
                ApplicationInsights.TrackEvent(`cooledProductConflictResolution`, {
                    kept: 'conflicted',
                    keptCooled: !mainProduct?.isCooled
                });

                if (mainProduct && mainProduct.isRx) {
                    removeItems([mainProduct.code], onConflictedKept);
                    return;
                }

                clearModal();
                break;
            }
        }
    };

    useEffect(() => {
        window.addEventListener(EventNames.CooledProductsConflict, onConflict);
        window.addEventListener(EventNames.CartChanged, onCartChanged);
        window.addEventListener(EventNames.CartValidated, onCartChanged);

        return () => {
            window.removeEventListener(EventNames.CooledProductsConflict, onConflict);
            window.removeEventListener(EventNames.CartChanged, onCartChanged);
            window.removeEventListener(EventNames.CartValidated, onCartChanged);
        };
    }, []);

    return (
        <Modal
            variant={ModalVariant.Center}
            className="CooledProductsConflictModal"
            heading={getTranslation(keys['modal.cooledProducts.heading'])}
            closeLabel={getTranslation(keys['modal.cancel'])}
            confirmLabel={getTranslation(keys['modal.cooledProducts.keep'])}
            errorMessage={error}
            isVisible={!!isVisible}
            onClose={onClose}
            onConfirm={handleConfirm}
            showConfirmLoader={isLoading}
        >
            <div className="CooledProductsConflictModal__Text">
                <div className="CooledProductsConflictModal__Description">
                    {getTranslation(keys[mainProduct?.isCooled ? 'modal.cooledProducts.descriptionCooled' : 'modal.cooledProducts.descriptionWarm'])}
                </div>
                <div className="CooledProductsConflictModal__SelectProducts">{getTranslation(keys['modal.cooledProducts.selectProducts'])}</div>
            </div>
            <div className="CooledProductsConflictModal__Entries" role="radiogroup">
                {sameTemperatureProducts && (
                    <CooledProductsConflictEntry
                        mainProductIncluded
                        isSelected={selectedEntry === SelectedEntry.This}
                        onSelect={() => handleSelect(SelectedEntry.This)}
                        products={sameTemperatureProducts}
                        buttonLabel={getTranslation(
                            keys[mainProduct?.isCooled ? 'modal.cooledProducts.selectCooledProduct' : 'modal.cooledProducts.selectWarmProduct']
                        )}
                        pictureProfile={pictureProfile}
                    />
                )}
                {conflictedProducts && (
                    <CooledProductsConflictEntry
                        mainProductIncluded={false}
                        isSelected={selectedEntry === SelectedEntry.Conflicted}
                        onSelect={() => handleSelect(SelectedEntry.Conflicted)}
                        products={conflictedProducts}
                        singleLabel={getTranslation(
                            keys[
                                mainProduct?.isCooled
                                    ? 'modal.cooledProducts.singleConflictedWarmProduct'
                                    : 'modal.cooledProducts.singleConflictedCooledProduct'
                            ]
                        )}
                        multipleLabel={getTranslation(
                            keys[
                                mainProduct?.isCooled
                                    ? 'modal.cooledProducts.multipleConflictedWarmProducts'
                                    : 'modal.cooledProducts.multipleConflictedCooledProducts'
                            ]
                        )}
                        buttonLabel={getTranslation(
                            keys[mainProduct?.isCooled ? 'modal.cooledProducts.selectWarmProduct' : 'modal.cooledProducts.selectCooledProduct']
                        )}
                        pictureProfile={pictureProfile}
                    />
                )}
            </div>
        </Modal>
    );
};
